import { createSelector } from 'reselect';

import {
  DELAYED_SHIPPING_URLS_TOKEN,
  DIGITAL_GIFT_URLS_TOKEN,
  EMAIL_TEMPLATE_TYPE_BY_TOKEN,
} from '../../../constants/createSendEmail';
import { IEmailTokenGate } from '../../../types/createSendEmail';
import { EmailTemplateTypesEnum } from '../../../types/templates';
import { IAppState } from '../../reducers';
import { selectCsIsContentDigital, selectCsSelectedKit } from './contents';
import { selectIsClaimBased } from './recipients';

export { DELAYED_SHIPPING_URLS_TOKEN, DIGITAL_GIFT_URLS_TOKEN };

export const selectCsEmailState = (state: IAppState) => state.createSend.email;

export const selectCsEmailInitialized = createSelector(selectCsEmailState, (email) => email.initialized);

export const selectCsEmailTemplateId = createSelector(selectCsEmailState, (email) => email.emailTemplateId);

export const selectCsEmailSubject = createSelector(selectCsEmailState, (email) => email.subject);

export const selectCsEmailBody = createSelector(selectCsEmailState, (email) => email.body);

export const selectCsEmailPrevTemplateId = createSelector(selectCsEmailState, (email) => email.previousTemplateId);

export const selectCsEmailIsOn = createSelector(selectCsEmailTemplateId, (emailTemplateId) => emailTemplateId !== null);

export const selectCsRequiredEmailToken = createSelector(
  selectCsIsContentDigital,
  selectIsClaimBased,
  (isContentDigital, isClaimBased): string | null => {
    if (isClaimBased) {
      return DELAYED_SHIPPING_URLS_TOKEN;
    }

    return isContentDigital ? DIGITAL_GIFT_URLS_TOKEN : null;
  },
);

export const selectCsEmailTemplateType = createSelector(
  selectCsRequiredEmailToken,
  (token): EmailTemplateTypesEnum | null => (token === null ? null : EMAIL_TEMPLATE_TYPE_BY_TOKEN[token]),
);

export const selectCsKitEmailTemplateDefault = createSelector(
  selectCsSelectedKit,
  selectCsEmailTemplateType,
  (kit, templateType): string =>
    (templateType === EmailTemplateTypesEnum.DIGITAL_GIFT
      ? kit?.digital_link_template_default
      : kit?.delayed_shipping_template_default) ?? '',
);

export const selectCsEmailTokenGate = createSelector(
  selectCsEmailIsOn,
  selectCsEmailBody,
  selectCsRequiredEmailToken,
  (isOn, body, required): IEmailTokenGate =>
    required === null
      ? { required: null, isMissing: false }
      : { required, isMissing: isOn && !body.includes(required) },
);

export const selectCsIsEmailStepValid = createSelector(
  selectCsEmailTemplateId,
  selectCsEmailSubject,
  selectCsEmailBody,
  selectCsEmailTokenGate,
  (emailTemplateId, subject, body, tokenGate): boolean => {
    if (emailTemplateId === null) {
      return true;
    }

    if (!subject.trim() || !body.trim()) {
      return false;
    }

    return !tokenGate.isMissing;
  },
);

export const selectCsEmailReminder = createSelector(selectCsEmailState, (email) => email.reminder);

export const selectCsReminderInterval = createSelector(selectCsEmailReminder, (r) => r.interval);

export const selectCsReminderIntervalDraft = createSelector(selectCsEmailReminder, (r) => r.intervalDraft);

export const selectCsReminderCustomDaysDraft = createSelector(selectCsEmailReminder, (r) => r.customDaysDraft);
